import * as React from "react";
import { Plus } from "lucide-react";
import { SearchablePopover } from "@racoon/ui";
import { IcCancel } from "@racoon/icons";

type Item = { id: string; name: string };
/** A chip that cannot be removed, e.g. one the backend assigns itself. */
type Chip = Item & { locked?: boolean };

type ChipsPickerFieldProps = {
  /** What the user is in now, drawn as removable chips. */
  chips: Chip[];
  /** What can be added; filtered by the popover's search. */
  options: Item[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  busy?: boolean;
  inputId: string;
  texts: {
    add: string;
    search: string;
    empty: string;
    noResults: string;
    remove: (name: string) => string;
  };
};

/** Chips with a "+" search popover; the caller applies each add / remove itself and labels it. */
export function ChipsPickerField({
  chips,
  options,
  onAdd,
  onRemove,
  busy,
  inputId,
  texts,
}: ChipsPickerFieldProps) {
  const [search, setSearch] = React.useState("");
  const [popoverOpen, setPopoverOpen] = React.useState(false);

  const found = options.filter((o) => o.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="bg-white border border-input rounded-lg p-1.5 flex flex-wrap gap-2 items-start">
      {chips.map((chip) => (
        <div
          key={chip.id}
          className={`flex items-center gap-1.5 pl-2.5 ${chip.locked ? "pr-2.5" : "pr-1"} bg-brand-blue rounded-full h-6 text-white text-xs font-medium`}
        >
          <span>{chip.name}</span>
          {!chip.locked && (
            <button
              type="button"
              aria-label={texts.remove(chip.name)}
              onClick={() => onRemove(chip.id)}
              disabled={busy}
            >
              <IcCancel className="w-4 h-4"/>
            </button>
          )}
        </div>
      ))}
      <SearchablePopover
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        trigger={
          <button
            type="button"
            className="flex items-center justify-center w-6 h-6 rounded-full bg-brand-blue hover:opacity-80 transition-opacity"
            title={texts.add}
          >
            <Plus size={14} className="text-white" strokeWidth={2.5} />
          </button>
        }
        searchValue={search}
        onSearchChange={setSearch}
        inputId={inputId}
        placeholder={texts.search}
        listClassName="space-y-1"
      >
        {found.length > 0 ? (
          found.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                onAdd(option.id);
                setSearch("");
                setPopoverOpen(false);
              }}
              disabled={busy}
              className="w-full text-left px-3 py-2 rounded hover:bg-gray-100 disabled:opacity-50 text-sm"
            >
              {option.name}
            </button>
          ))
        ) : (
          <p className="text-xs text-muted-foreground py-2">
            {search ? texts.noResults : texts.empty}
          </p>
        )}
      </SearchablePopover>
    </div>
  );
}
